import { useState, useRef, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../context/AuthContext'
import PersonaAvatar from './PersonaAvatar'
import { useRoomTone } from './RoomTone'
import { useSSEConnected } from '../hooks/useSSE'

const INTERNAL_LINKS = [
  { to: '/conversations', label: 'Talk' },
  { to: '/feed', label: 'Feed' },
  { to: '/projects', label: 'Projects' },
  { to: '/agora', label: 'Agora' },
  { to: '/paper', label: 'Paper' },
  { to: '/evolve', label: 'Evolve' },
  { to: '/wiki', label: 'Wiki' },
  { to: '/tech', label: 'Apps' },
  { to: '/operations', label: 'Operations' },
  { to: '/activity', label: 'Activity' },
  { to: '/awareness', label: 'Awareness' },
  { to: '/clients', label: 'Communities' },
]

const COMMUNITY_LINKS = [
  { to: '/', label: 'Lobby' },
  { to: '/conversations', label: 'Talk' },
  { to: '/projects', label: 'Projects' },
  { to: '/evolve', label: 'Evolve' },
  { to: '/tech', label: 'Apps' },
  { to: '/status', label: 'Operations' },
]

interface SearchResult { type: string; name: string; id?: string; display_name?: string }

export default function NavBar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [showSearch, setShowSearch] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  const sseOk = useSSEConnected()

  if (!user) return null

  const isCommunity = user.userType === 'community'
  const communityDisplayName = isCommunity ? user.communityName : null

  // Fetch nav permissions for community users
  const navCommunityId = isCommunity ? (user.communityId || `portal-${user.communitySlug}`) : ''
  const { data: navPerms } = useQuery({
    queryKey: ['nav-permissions', navCommunityId],
    queryFn: async () => {
      const res = await fetch(`/api/communities/${navCommunityId}/objects?type=nav_permissions`, { credentials: 'include' })
      if (!res.ok) return null
      const data = await res.json()
      const perms = Array.isArray(data) ? data : (data.data || null)
      if (Array.isArray(perms) && perms.length > 0) {
        const first = perms[0]
        const d = typeof first.data === 'string' ? JSON.parse(first.data) : first.data
        return Array.isArray(d) ? d : null
      }
      return null
    },
    enabled: isCommunity && !!navCommunityId,
    staleTime: 300_000,
  })

  // Route label map for permission matching
  const ROUTE_LABELS: Record<string, string> = {
    '/conversations': 'talk', '/feed': 'feed', '/projects': 'projects',
    '/': 'lobby', '/tech': 'tech', '/agora': 'agora', '/paper': 'paper',
    '/ceremony': 'ceremony', '/wiki': 'wiki', '/operations': 'operations',
    '/activity': 'activity', '/awareness': 'awareness', '/clients': 'clients',
    '/status': 'operations',
  }

  let links = isCommunity ? COMMUNITY_LINKS : INTERNAL_LINKS
  if (isCommunity && navPerms) {
    links = INTERNAL_LINKS.filter(link => {
      const key = ROUTE_LABELS[link.to] || link.label.toLowerCase()
      return navPerms.includes(key)
    })
    links = links.map(l => l.to === '/tech' ? { ...l, label: 'Apps' } : l)
    links = links.map(l => l.to === '/operations' ? { ...l, to: '/status' } : l)
  } else if (isCommunity) {
    links = links.map(l => l.to === '/operations' ? { ...l, to: '/status' } : l)
  }
  if (!user?.isOperator && user?.role !== 'admin') {
    links = links.map(l => l.to === '/operations' ? { ...l, to: '/status' } : l)
  }
  const { active: roomToneActive, toggle: toggleRoomTone } = useRoomTone()

  // Fetch total unread count for nav badge (separate query key to avoid cache conflicts)
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
  const communityId = isCommunity ? user.communityId || `portal-${user.communitySlug}` : 'internal'
  const { data: convData } = useQuery({
    queryKey: ['communityConversations', communityId],
    queryFn: async () => {
      const res = await fetch('/api/conversations?limit=200', { headers: authHeaders })
      if (!res.ok) return { conversations: [] }
      const data = await res.json()
      const all = Array.isArray(data) ? data : (data.conversations || [])
      return { conversations: all }
    },
    refetchInterval: sseOk ? false : 60_000,
  })
  const totalUnread = (convData?.conversations || []).reduce((sum: number, c: any) => sum + (c.unread_count || 0), 0)

  // Feed pending count for badge
  const { data: feedStats } = useQuery({
    queryKey: ['feedStats'],
    queryFn: async () => {
      const r = await fetch('/api/feed/stats')
      if (!r.ok) return { pending: 0 }
      return r.json()
    },
    refetchInterval: 15_000,
  })
  const feedPending = feedStats?.cards_pending || 0

  async function handleSearch(q: string) {
    setSearchQuery(q)
    if (q.length < 2) { setSearchResults([]); return }
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`)
      if (res.ok) {
        const data = await res.json()
        setSearchResults(data.results || [])
        setShowSearch(true)
      }
    } catch { /* ignore */ }
  }

  function handleResultClick(result: SearchResult) {
    setShowSearch(false)
    setSearchQuery('')
    if (result.type === 'persona') navigate(`/terminal/${result.name}`)
    else if (result.type === 'project') navigate(`/project/${result.id}`)
    else if (result.type === 'conversation') navigate(`/conversation/${result.id}`)
  }

  // Close search on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearch(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <nav
      className="flex items-center gap-y-0.5 px-3 py-1.5 shrink-0 relative"
      style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)', zIndex: 50, minHeight: 44 }}
    >
      <NavLink to="/" className="mr-2 shrink-0 no-underline" style={{ color: 'var(--accent)', lineHeight: 1.2 }}>
        <div className="text-sm font-semibold">The Gathering</div>
        {communityDisplayName && (
          <div style={{ fontSize: 13, color: '#FFFFFF', fontWeight: 700, letterSpacing: '0.5px', textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>{communityDisplayName}</div>
        )}
      </NavLink>

      <div className="flex items-center gap-x-0.5 overflow-x-auto min-w-0 flex-1" style={{ scrollbarWidth: 'none' }}>
      {links.filter(link => link.to !== '/operations' || user?.isOperator || user?.role === 'admin').map(link => {
        const isOpsOnRemote = (link.to === '/operations' || link.to === '/status') && location.hostname !== 'localhost' && !location.hostname.includes('100.');
        if (isOpsOnRemote) {
          const opsPath = link.to;
          return (
            <a
              key={link.to}
              href={`http://100.70.172.65:5280${opsPath}`}
              className="px-1.5 py-1 font-medium rounded-md transition-colors whitespace-nowrap no-underline shrink-0"
              style={{ color: 'var(--text-secondary)', fontSize: 11 }}
            >
              {link.label}
            </a>
          );
        }
        return (
        <NavLink
          key={link.to}
          to={link.to}
          end={link.to === '/'}
          className="px-1.5 py-1 font-medium rounded-md transition-colors whitespace-nowrap no-underline shrink-0"
          style={({ isActive }) => ({
            color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
            background: isActive ? 'var(--bg-elevated)' : 'transparent',
            position: 'relative' as const,
            fontSize: 11,
          })}
        >
          {link.label}
          {link.to === '/conversations' && totalUnread > 0 && (
            <span style={{
              position: 'absolute',
              top: -4,
              right: -6,
              background: '#E53935',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}>
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
          {link.to === '/feed' && feedPending > 0 && (
            <span style={{
              position: 'absolute',
              top: -4,
              right: -6,
              background: '#856FC8',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}>
              {feedPending > 99 ? '99+' : feedPending}
            </span>
          )}
        </NavLink>
        );
      })}
      </div>

      {/* Search */}
      {!isCommunity && (
        <div ref={searchRef} className="relative shrink-0">
          <input
            type="text"
            value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            onFocus={() => { if (searchResults.length) setShowSearch(true) }}
            placeholder="Search..."
            className="w-24 sm:w-36 px-2 py-1 text-xs rounded-md outline-none"
            style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
          {showSearch && searchResults.length > 0 && (
            <div
              className="absolute top-full right-0 mt-1 w-64 rounded-lg overflow-hidden shadow-lg z-50"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
            >
              {searchResults.slice(0, 8).map((r, i) => (
                <button
                  key={i}
                  onClick={() => handleResultClick(r)}
                  className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs hover:bg-[var(--bg-elevated)] transition-colors"
                >
                  {r.type === 'persona' && <PersonaAvatar name={r.name} size={18} />}
                  <span style={{ color: 'var(--text-primary)' }}>{r.display_name || r.name || r.id}</span>
                  <span className="ml-auto capitalize" style={{ color: 'var(--text-muted)' }}>{r.type}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isCommunity && (
        <button
          onClick={toggleRoomTone}
          className="text-xs px-2.5 py-1 rounded-md shrink-0 transition-colors"
          style={{
            background: roomToneActive ? 'rgba(212,160,84,0.15)' : 'transparent',
            color: roomToneActive ? '#D4A054' : 'var(--text-muted)',
            border: roomToneActive ? '1px solid rgba(212,160,84,0.3)' : '1px solid transparent',
          }}
          title="Room Tone — ambient studio presence"
        >
          <span style={{ display: 'inline-block', animation: roomToneActive ? 'pulse 2s ease-in-out infinite' : 'none' }}>
            Room Tone
          </span>
        </button>
      )}

      {/* Back to Gathering — shown when an operator is viewing a community */}
      {isCommunity && user.isOperator && (
        <button
          onClick={async () => {
            // Try to restore saved operator JWT
            const operatorJwt = localStorage.getItem('operator-jwt')
            if (operatorJwt) {
              localStorage.setItem('session_token', operatorJwt)
              document.cookie = `session=${operatorJwt}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`
              localStorage.removeItem('operator-jwt')
              window.location.href = '/'
            } else {
              // No saved JWT — request a fresh operator session from the community-view API for 'gathering'
              try {
                const res = await fetch('/api/community-view/gathering')
                const data = await res.json()
                if (data.token) {
                  localStorage.setItem('session_token', data.token)
                  document.cookie = `session=${data.token}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`
                  window.location.href = '/'
                  return
                }
              } catch { /* fallback below */ }
              // Last resort: clear and go to login
              localStorage.removeItem('session_token')
              document.cookie = 'session=; Max-Age=0; path=/'
              window.location.href = '/login'
            }
          }}
          className="text-xs px-2.5 py-1 rounded-md shrink-0"
          style={{ background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer' }}
        >
          ← Back to Gathering
        </button>
      )}

      <NavLink
        to="/settings"
        className="text-xs px-2.5 py-1 rounded-md shrink-0 no-underline flex items-center gap-1.5"
        style={({ isActive }) => ({
          color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
          background: isActive ? 'var(--bg-elevated)' : 'transparent',
        })}
      >
        <span>⚙</span>
        <span>{user.userName}</span>
      </NavLink>
      <button onClick={logout} className="text-xs px-2 py-1 rounded hover:bg-[var(--bg-elevated)] transition-colors shrink-0" style={{ color: 'var(--text-muted)' }}>
        Sign out
      </button>
    </nav>
  )
}
